import React, { useEffect, useState } from 'react';
import { Diagram, DiagramHub, DiagramNode } from '../types';

const Node: React.FC<DiagramNode> = ({ x, y, title, sub }) => (
  <g>
    <rect x={x} y={y} width={120} height={56} rx={10} className="fill-ink-800 stroke-ink-600" />
    <text x={x + 14} y={y + 24} className="fill-fg font-mono text-[11px]">
      {title}
    </text>
    <text x={x + 14} y={y + 41} className="fill-fg-faint font-mono text-[9.5px]">
      {sub}
    </text>
  </g>
);

const Hub: React.FC<DiagramHub> = ({ x, y, w, h, title, chips, notes }) => {
  const chipW = (w - 36) / 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} className="fill-ink-800 stroke-accent/40" />
      <text x={x + 14} y={y + 23} className="fill-fg font-mono text-[11px]">
        {title}
      </text>
      {chips.map((chip, i) => {
        const cx = x + 14 + i * (chipW + 8);
        return (
          <g key={chip}>
            <rect x={cx} y={y + 34} width={chipW} height={24} rx={6} className="fill-accent/10 stroke-accent/40" />
            <text x={cx + chipW / 2} y={y + 49.5} textAnchor="middle" className="fill-accent font-mono text-[9.5px]">
              {chip}
            </text>
          </g>
        );
      })}
      {notes.map((note, i) => (
        <text key={note} x={x + 14} y={y + 81 + i * 14} className="fill-fg-faint font-mono text-[9px]">
          {note}
        </text>
      ))}
    </g>
  );
};

const Packet: React.FC<{ path: string; dur: string; begin?: string }> = ({ path, dur, begin = '0s' }) => (
  <g>
    <circle r={7} className="fill-accent/20">
      <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={path} />
    </circle>
    <circle r={3} className="fill-accent">
      <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={path} />
    </circle>
  </g>
);

/** Animated architecture diagram: packets flow along edges and slide under the node boxes. */
const ArchDiagram: React.FC<{ diagram: Diagram }> = ({ diagram }) => {
  const [logIndex, setLogIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setLogIndex((i) => (i + 1) % diagram.logs.length), 2400);
    return () => window.clearInterval(id);
  }, [diagram.logs.length]);

  return (
    <figure className="relative">
      <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-accent/25 via-ink-700/40 to-transparent" aria-hidden />
      <div className="relative overflow-hidden rounded-xl bg-ink">
        <div className="flex items-center justify-between border-b border-ink-700 px-4 py-3 font-mono text-[11px] text-fg-faint">
          <span>
            ~/{diagram.repo} <span className="text-fg-muted">· {diagram.caption}</span>
          </span>
          <span className="flex items-center gap-1.5 text-accent">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
            {diagram.status}
          </span>
        </div>

        <svg viewBox={`0 0 480 ${diagram.height}`} className="h-auto w-full" role="img" aria-label={diagram.ariaLabel}>
          {diagram.edges.map((edge) => (
            <g key={edge.d}>
              <path d={edge.d} fill="none" strokeWidth={1.25} className="stroke-ink-600" />
              <path d={edge.d} fill="none" strokeWidth={1.25} className="flow stroke-accent/40" />
            </g>
          ))}

          {diagram.packets.map((packet, i) => (
            <Packet key={i} {...packet} />
          ))}

          {diagram.nodes.map((node) => (
            <Node key={node.title} {...node} />
          ))}
          <Hub {...diagram.hub} />

          {diagram.edges.map(
            (edge) =>
              edge.label && (
                <text
                  key={edge.label.text}
                  x={edge.label.x}
                  y={edge.label.y}
                  textAnchor="middle"
                  className="fill-fg-faint font-mono text-[9px]"
                >
                  {edge.label.text}
                </text>
              )
          )}
        </svg>

        <div className="flex items-center gap-2 border-t border-ink-700 px-4 py-3 font-mono text-[11px]">
          <span className="text-accent">›</span>
          <span key={logIndex} className="animate-log truncate whitespace-pre text-fg-muted">
            {diagram.logs[logIndex]}
          </span>
        </div>
      </div>
    </figure>
  );
};

export default ArchDiagram;
